// Drive-Thru Demo — POS-screen workspace UI (Tealive / Bask Bear).
//
// Demo workspaces (`company_type == "drive-thru"` from /auth/me) land here
// instead of the recruiter UI: the user starts a voice call with the AI crew
// agent (ElevenLabs SDK, browser mic) and the screen fills in order lines and
// a running total live — like the order-confirmation screen at a real
// drive-thru. On verbal confirmation the order locks with the authoritative
// server-computed total. No transcript UI.
//
// Flow: GET /drive-thru/config → GET /drive-thru/signed-url (single-use,
// minted per attempt; no language param — one bilingual agent per brand that
// mirrors the caller's language) → conversation.startSession({ signedUrl })
// → onConnect(conversationId) → poll GET /drive-thru/order every ~1s
// (none → draft → ok|partial|rejected) → lock screen, stop polling.
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ConversationProvider, useConversation } from "@elevenlabs/react";
import {
    CarIcon,
    HistoryIcon,
    Loader2Icon,
    MicIcon,
    MicOffIcon,
    PhoneOffIcon,
    PlusIcon,
    RotateCcwIcon,
    XIcon,
} from "lucide-react";
import { Button } from "@component/button";
import {
    DriveThruSoldOutError,
    getDriveThruConfig,
    getDriveThruMenu,
    getDriveThruOrder,
    getDriveThruOrders,
    getDriveThruSignedUrl,
    setDriveThruSoldOut,
} from "@service/drive-thru";
import type {
    DriveThruBrand,
    DriveThruConfig,
    DriveThruMenu,
    DriveThruOrder,
    DriveThruOrderStatus,
} from "@type/drive-thru";
import { catchErrorLog } from "@util/sentry";
import { cn } from "@util/tailwind";

// Prices are seeded in MYR (both demo brands are Malaysian).
function rm(v: number): string {
    return `RM ${v.toFixed(2)}`;
}

// Per-brand accents; the BE picks the brand server-side (drive_thru_brand).
const BRAND_THEME: Record<
    DriveThruBrand,
    { header: string; accent: string; ring: string }
> = {
    tealive: {
        header: "bg-purple-800",
        accent: "bg-purple-700 hover:bg-purple-800",
        ring: "ring-purple-500",
    },
    baskbear: {
        header: "bg-amber-900",
        accent: "bg-amber-800 hover:bg-amber-900",
        ring: "ring-amber-500",
    },
};

// The shadcn `outline` variant is unreadable on this dark screen: it paints a
// white `bg-background` but sets no text color, so the label inherits the
// page's light text — white-on-white. Use these classes instead for outline
// buttons that sit on the gray-900/950 panels.
const DARK_OUTLINE_BTN =
    "border-gray-600 bg-gray-800 text-gray-100 hover:bg-gray-700 hover:text-white";

type Phase = "idle" | "connecting" | "live" | "final" | "abandoned";

const FINAL_STATUSES: DriveThruOrderStatus[] = ["ok", "partial", "rejected"];

// useConversation reads its state from ConversationProvider — same wrapper
// pattern as el-voice-interview / interview-voice-only-active.
export default function DriveThruDemoPage() {
    return (
        <ConversationProvider>
            <DriveThruPosScreen />
        </ConversationProvider>
    );
}

function DriveThruPosScreen() {
    const navigate = useNavigate();

    const [config, setConfig] = useState<DriveThruConfig | null>(null);
    const [configError, setConfigError] = useState<string | null>(null);

    const [phase, setPhase] = useState<Phase>("idle");
    const [micDenied, setMicDenied] = useState(false);
    const [callError, setCallError] = useState<string | null>(null);

    const [order, setOrder] = useState<DriveThruOrder | null>(null);
    const [orderStatus, setOrderStatus] = useState<DriveThruOrderStatus>("none");

    const [history, setHistory] = useState<DriveThruOrder[]>([]);
    const [menu, setMenu] = useState<DriveThruMenu | null>(null);
    const [soldOut, setSoldOut] = useState<string[]>([]);
    const [soldOutError, setSoldOutError] = useState<string | null>(null);
    const [soldOutSaving, setSoldOutSaving] = useState(false);

    // Refs so the SDK callbacks (stable identities) always see current values.
    const conversationIdRef = useRef<string>("");
    const statusRef = useRef<DriveThruOrderStatus>("none");
    const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const phaseRef = useRef<Phase>("idle");
    phaseRef.current = phase;

    // Single-flight + stale-completion guards for the order poll: `pollSeqRef`
    // is bumped whenever polling (re)starts or stops, so a response from a
    // previous polling run can never write state. `pollInFlightRef` holds the
    // seq of the run that currently has a request in flight (null = none) —
    // keying the lock to the RUN means a hung request from a previous run can
    // never starve the next one, and a stale completion can't clear a lock it
    // no longer owns.
    const pollSeqRef = useRef(0);
    const pollInFlightRef = useRef<number | null>(null);

    const stopPolling = useCallback(() => {
        if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
        }
        // Invalidate any request already in flight — its completion is ignored.
        pollSeqRef.current++;
    }, []);

    const refreshHistory = useCallback(() => {
        getDriveThruOrders(20)
            .then(setHistory)
            .catch((err) => catchErrorLog(`[drive-thru] history load failed: ${err}`));
    }, []);

    // Poll the order for the active call every ~1s. The decision lives here (not
    // in render) so a final status immediately locks the screen and stops the
    // interval — the agent then ends the call itself (onDisconnect fires).
    // Single-flight: a tick is skipped while a request is in flight, and a
    // completion is dropped when polling was stopped/restarted meanwhile — so a
    // slow stale `draft` can never overwrite an already-final locked screen.
    const startPolling = useCallback(
        (elConversationId: string) => {
            stopPolling();
            const seq = pollSeqRef.current;
            const runPoll = async () => {
                // Single-flight WITHIN this run only: a hung request from a
                // previous run (different seq) never blocks this one.
                if (pollInFlightRef.current === seq) return;
                pollInFlightRef.current = seq;
                try {
                    const poll = await getDriveThruOrder(elConversationId);
                    if (seq !== pollSeqRef.current) return; // stale run — ignore
                    statusRef.current = poll.status;
                    setOrderStatus(poll.status);
                    if (poll.order) setOrder(poll.order);
                    if (FINAL_STATUSES.includes(poll.status)) {
                        stopPolling();
                        setPhase("final");
                        refreshHistory();
                    }
                } catch (err) {
                    // Transient poll failures are fine (next tick retries); only log.
                    catchErrorLog(`[drive-thru] order poll failed: ${err}`);
                } finally {
                    // Release only if this run still owns the lock — a stale
                    // completion must not clear the lock of a newer run.
                    if (pollInFlightRef.current === seq) {
                        pollInFlightRef.current = null;
                    }
                }
            };
            void runPoll();
            pollTimerRef.current = setInterval(() => {
                void runPoll();
            }, 1000);
        },
        [stopPolling, refreshHistory]
    );

    const conversation = useConversation({
        onConnect: ({ conversationId }: { conversationId: string }) => {
            conversationIdRef.current = conversationId;
            statusRef.current = "none";
            setPhase("live");
            startPolling(conversationId);
        },
        onDisconnect: () => {
            stopPolling();
            // The agent finalizes the order and hangs up itself — which can land
            // BETWEEN poll ticks. Reconcile once against /drive-thru/order before
            // declaring a drive-off, so a confirmed order is never shown as
            // abandoned just because the last poll predates finalization.
            const reconcile = async () => {
                // Poll already saw a final status → the screen is locked; done.
                if (FINAL_STATUSES.includes(statusRef.current)) return;
                // Only a live/connecting call can be abandoned (not an already
                // reset/idle screen).
                if (phaseRef.current !== "live" && phaseRef.current !== "connecting") {
                    return;
                }
                const conversationId = conversationIdRef.current;
                if (conversationId) {
                    try {
                        const poll = await getDriveThruOrder(conversationId);
                        statusRef.current = poll.status;
                        setOrderStatus(poll.status);
                        if (poll.order) setOrder(poll.order);
                        if (FINAL_STATUSES.includes(poll.status)) {
                            setPhase("final");
                            refreshHistory();
                            return;
                        }
                    } catch (err) {
                        catchErrorLog(
                            `[drive-thru] final order reconcile failed: ${err}`
                        );
                    }
                }
                // A car that "drives off": the call ended without a final status —
                // the draft order is abandoned (it never reaches history).
                setPhase("abandoned");
            };
            void reconcile();
        },
        onError: (message: unknown) => {
            catchErrorLog(`[drive-thru] EL conversation error: ${message}`);
        },
    });

    // Initial load: config (brand, languages) + history + menu (sold-out picker).
    // A 403 on any endpoint means this user/workspace isn't a drive-thru demo
    // account — route back to login per the contract.
    useEffect(() => {
        let cancelled = false;
        getDriveThruConfig()
            .then((cfg) => {
                if (cancelled) return;
                setConfig(cfg);
                setSoldOut(cfg.menu_summary?.sold_out ?? []);
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                const status = (err as { response?: { status?: number } })?.response
                    ?.status;
                if (status === 403) {
                    navigate("/");
                    return;
                }
                catchErrorLog(`[drive-thru] config load failed: ${err}`);
                setConfigError("Could not load the demo workspace. Try refreshing.");
            });
        getDriveThruOrders(20)
            .then((orders) => {
                if (!cancelled) setHistory(orders);
            })
            .catch((err) => catchErrorLog(`[drive-thru] history load failed: ${err}`));
        getDriveThruMenu()
            .then((m) => {
                if (cancelled) return;
                setMenu(m);
                // Sync the panel with the server's sold-out list (config's
                // menu_summary, loaded in parallel, carries the same state).
                setSoldOut(m.sold_out ?? []);
            })
            .catch((err) => catchErrorLog(`[drive-thru] menu load failed: ${err}`));
        return () => {
            cancelled = true;
            stopPolling();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function startCall() {
        setCallError(null);
        setMicDenied(false);
        // Mic permission is required before the SDK can stream — surface a
        // blocking prompt when denied (the call can't start without it).
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            stream.getTracks().forEach((t) => t.stop());
        } catch {
            setMicDenied(true);
            return;
        }
        setPhase("connecting");
        setOrder(null);
        setOrderStatus("none");
        statusRef.current = "none";
        conversationIdRef.current = "";
        try {
            // Single-use URL — mint fresh for every attempt/retry.
            const { signed_url } = await getDriveThruSignedUrl();
            await conversation.startSession({ signedUrl: signed_url });
        } catch (err: unknown) {
            const status = (err as { response?: { status?: number } })?.response
                ?.status;
            if (status === 403) {
                navigate("/");
                return;
            }
            catchErrorLog(`[drive-thru] call start failed: ${err}`);
            setCallError("Could not start the call. Please try again.");
            setPhase("idle");
        }
    }

    async function endCall() {
        try {
            await conversation.endSession();
        } catch {
            // onDisconnect handles state either way.
        }
    }

    // "New customer" — reset the POS screen for the next car.
    function resetForNextCustomer() {
        stopPolling();
        setOrder(null);
        setOrderStatus("none");
        statusRef.current = "none";
        conversationIdRef.current = "";
        setCallError(null);
        setPhase("idle");
    }

    async function applySoldOut(next: string[]) {
        const prev = soldOut;
        setSoldOutError(null);
        setSoldOutSaving(true);
        setSoldOut(next); // optimistic
        try {
            // Adopt the authoritative list the BE saved, not the optimistic one.
            const saved = await setDriveThruSoldOut(next);
            setSoldOut(saved);
        } catch (err) {
            setSoldOut(prev); // rollback — nothing was saved server-side
            setSoldOutError(
                err instanceof DriveThruSoldOutError
                    ? err.unknownItems.length > 0
                        ? `${err.message}: ${err.unknownItems.join(", ")}`
                        : err.message
                    : "Could not update the sold-out list. Please try again."
            );
            catchErrorLog(`[drive-thru] sold-out update failed: ${err}`);
        } finally {
            setSoldOutSaving(false);
        }
    }

    const brand: DriveThruBrand = config?.brand ?? "tealive";
    const theme = BRAND_THEME[brand] ?? BRAND_THEME.tealive;
    const isFinal = phase === "final";

    if (configError) {
        return (
            <div className="min-h-screen bg-gray-950 flex items-center justify-center">
                <p className="text-sm text-gray-400">{configError}</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-950 text-gray-100 flex flex-col">
            {/* Brand header. No language toggle: a single bilingual agent per
                brand detects and mirrors the caller's language automatically. */}
            <header className={cn("px-6 py-4 flex items-center", theme.header)}>
                <div className="flex items-center gap-3">
                    <CarIcon className="w-6 h-6 text-white" />
                    <div>
                        <h1 className="text-lg font-bold text-white leading-tight">
                            {config?.brand_display ?? "Drive-Thru"}
                        </h1>
                        <p className="text-xs text-white/70">
                            {config?.outlet_name ?? "Drive-Thru (Demo)"}
                        </p>
                    </div>
                </div>
            </header>

            <main className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-6 p-6 max-w-[1400px] w-full mx-auto">
                {/* ── POS order screen ─────────────────────────────────── */}
                <section className="lg:col-span-2">
                    <div
                        className={cn(
                            "bg-white text-gray-900 rounded-xl overflow-hidden shadow-2xl min-h-[520px] flex flex-col",
                            isFinal && "ring-4",
                            isFinal && theme.ring
                        )}
                    >
                        {/* Order screen header */}
                        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between bg-gray-50">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                Order
                                {order?.order_no && (
                                    <span className="ml-2 text-gray-900 text-sm tabular-nums">
                                        {order.order_no}
                                    </span>
                                )}
                            </p>
                            <OrderStatusChip phase={phase} status={orderStatus} />
                        </div>

                        {/* Order lines */}
                        <div className="flex-1 px-5 py-4 overflow-y-auto">
                            {order && order.items.length > 0 ? (
                                <ul className="divide-y divide-gray-100">
                                    {order.items.map((item, i) => (
                                        <li key={`${item.item_code}-${i}`} className="py-3">
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="min-w-0">
                                                    <p className="text-sm font-medium text-gray-900">
                                                        <span className="tabular-nums mr-2">{item.quantity}×</span>
                                                        {item.name}
                                                    </p>
                                                    {item.modifiers.length > 0 && (
                                                        <ul className="mt-1 space-y-0.5">
                                                            {item.modifiers.map((m, mi) => (
                                                                <li
                                                                    key={mi}
                                                                    className="text-xs text-gray-500 flex justify-between"
                                                                >
                                                                    <span>• {m.name}</span>
                                                                    {m.price > 0 && (
                                                                        <span className="tabular-nums">
                                                                            +{rm(m.price)}
                                                                        </span>
                                                                    )}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                    {item.notes && (
                                                        <p className="mt-1 text-xs italic text-gray-400">
                                                            “{item.notes}”
                                                        </p>
                                                    )}
                                                </div>
                                                <span className="text-sm font-semibold tabular-nums flex-shrink-0">
                                                    {rm(item.line_total)}
                                                </span>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <EmptyOrderState phase={phase} />
                            )}

                            {/* Dropped items on a partial order */}
                            {isFinal && order && order.unavailable.length > 0 && (
                                <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                                    <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-1.5">
                                        Not available
                                    </p>
                                    <ul className="space-y-1">
                                        {order.unavailable.map((u, i) => (
                                            <li key={i} className="text-sm text-amber-800 flex justify-between">
                                                <span>{u.name}</span>
                                                <span className="text-xs text-amber-600">
                                                    {u.reason.replace(/_/g, " ")}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>

                        {/* Running / final total */}
                        <div className="px-5 py-4 border-t-2 border-dashed border-gray-200 bg-gray-50 flex items-center justify-between">
                            <span className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                                {isFinal ? "Total" : "Running total"}
                            </span>
                            <span
                                className={cn(
                                    "font-bold tabular-nums",
                                    isFinal ? "text-3xl text-gray-900" : "text-2xl text-gray-700"
                                )}
                            >
                                {rm(order?.accepted_total ?? 0)}
                            </span>
                        </div>
                    </div>

                    {/* Abandoned banner */}
                    {phase === "abandoned" && (
                        <div className="mt-3 rounded-lg border border-rose-800 bg-rose-950/60 px-4 py-3 flex items-center justify-between">
                            <p className="text-sm text-rose-200">
                                Order abandoned — the call ended before the customer confirmed.
                            </p>
                            <Button
                                size="sm"
                                variant="outline"
                                className={DARK_OUTLINE_BTN}
                                onClick={resetForNextCustomer}
                            >
                                <RotateCcwIcon className="w-4 h-4" />
                                Reset
                            </Button>
                        </div>
                    )}
                </section>

                {/* ── Controls + history ───────────────────────────────── */}
                <section className="space-y-4">
                    {/* Call control */}
                    <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
                            Crew agent
                        </p>
                        {micDenied && (
                            <div className="mb-3 rounded-lg border border-rose-800 bg-rose-950/60 px-3 py-2.5 flex items-start gap-2">
                                <MicOffIcon className="w-4 h-4 text-rose-300 mt-0.5 flex-shrink-0" />
                                <p className="text-xs text-rose-200">
                                    Microphone access is blocked. Allow the microphone in your
                                    browser settings — the call can't start without it.
                                </p>
                            </div>
                        )}
                        {callError && (
                            <p className="mb-3 text-xs text-rose-300">{callError}</p>
                        )}
                        {phase === "idle" || phase === "abandoned" ? (
                            <Button
                                className={cn("w-full text-white", theme.accent)}
                                onClick={() => {
                                    if (phase === "abandoned") resetForNextCustomer();
                                    startCall();
                                }}
                            >
                                <MicIcon className="w-4 h-4" />
                                Start taking order
                            </Button>
                        ) : isFinal ? (
                            <Button
                                className={cn("w-full text-white", theme.accent)}
                                onClick={resetForNextCustomer}
                            >
                                <PlusIcon className="w-4 h-4" />
                                New customer
                            </Button>
                        ) : (
                            <div className="space-y-3">
                                <div className="flex items-center gap-2 text-sm text-gray-300">
                                    {phase === "connecting" ? (
                                        <>
                                            <Loader2Icon className="w-4 h-4 animate-spin" />
                                            Connecting…
                                        </>
                                    ) : (
                                        <>
                                            <span className="relative flex h-2.5 w-2.5">
                                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                                                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                                            </span>
                                            Live — taking the order
                                        </>
                                    )}
                                </div>
                                {/* destructive (red) — readable on the dark
                                    panel in both enabled and disabled states */}
                                <Button
                                    variant="destructive"
                                    className="w-full"
                                    onClick={endCall}
                                >
                                    <PhoneOffIcon className="w-4 h-4" />
                                    End call
                                </Button>
                            </div>
                        )}
                    </div>

                    {/* Sold-out demo control */}
                    <SoldOutPanel
                        menu={menu}
                        soldOut={soldOut}
                        disabled={soldOutSaving}
                        error={soldOutError}
                        onApply={applySoldOut}
                    />

                    {/* Order history */}
                    <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                            <HistoryIcon className="w-3.5 h-3.5" />
                            Recent orders
                        </p>
                        {history.length === 0 ? (
                            <p className="text-xs text-gray-500">No finalized orders yet.</p>
                        ) : (
                            <ul className="divide-y divide-gray-800 max-h-72 overflow-y-auto">
                                {history.map((o) => (
                                    <li
                                        key={o.order_no}
                                        className="py-2.5 flex items-center justify-between gap-3"
                                    >
                                        <div className="min-w-0">
                                            <p className="text-sm text-gray-200 tabular-nums">{o.order_no}</p>
                                            <p className="text-xs text-gray-500 truncate">
                                                {o.items.length} item{o.items.length !== 1 ? "s" : ""} ·{" "}
                                                {o.status}
                                            </p>
                                        </div>
                                        <span className="text-sm font-semibold text-gray-200 tabular-nums flex-shrink-0">
                                            {rm(o.accepted_total)}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </section>
            </main>
        </div>
    );
}

function OrderStatusChip({
    phase,
    status,
}: {
    phase: Phase;
    status: DriveThruOrderStatus;
}) {
    if (phase === "final") {
        const map: Record<string, { label: string; cls: string }> = {
            ok: { label: "Confirmed", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
            partial: { label: "Confirmed (partial)", cls: "bg-amber-50 text-amber-700 border-amber-200" },
            rejected: { label: "Rejected", cls: "bg-rose-50 text-rose-700 border-rose-200" },
        };
        const m = map[status] ?? map.ok;
        return (
            <span className={cn("inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border", m.cls)}>
                {m.label}
            </span>
        );
    }
    if (phase === "live" || phase === "connecting") {
        return (
            <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border bg-blue-50 text-blue-700 border-blue-200">
                Taking order…
            </span>
        );
    }
    if (phase === "abandoned") {
        return (
            <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border bg-gray-100 text-gray-500 border-gray-200">
                Abandoned
            </span>
        );
    }
    return (
        <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border bg-gray-100 text-gray-500 border-gray-200">
            Waiting for customer
        </span>
    );
}

function EmptyOrderState({ phase }: { phase: Phase }) {
    return (
        <div className="h-full min-h-[280px] flex flex-col items-center justify-center text-center">
            <CarIcon className="w-10 h-10 text-gray-200 mb-3" />
            {phase === "live" || phase === "connecting" ? (
                <>
                    <p className="text-sm font-medium text-gray-500">Taking the order…</p>
                    <p className="text-xs text-gray-400 mt-1 max-w-xs">
                        Items appear here as the customer speaks. The first item can take
                        a few seconds.
                    </p>
                </>
            ) : (
                <>
                    <p className="text-sm font-medium text-gray-500">No active order</p>
                    <p className="text-xs text-gray-400 mt-1">
                        Start taking an order to see it fill in live.
                    </p>
                </>
            )}
        </div>
    );
}

// Demo control panel: simulate items going out of stock mid-demo. Sends the
// FULL sold-out list on every change; "Reset" sends []. `disabled` is true
// while a save is in flight (list replaces must not overlap); `error` is the
// rejection message from the last failed save (e.g. unknown item names).
function SoldOutPanel({
    menu,
    soldOut,
    disabled,
    error,
    onApply,
}: {
    menu: DriveThruMenu | null;
    soldOut: string[];
    disabled: boolean;
    error: string | null;
    onApply: (next: string[]) => void;
}) {
    const [picker, setPicker] = useState("");
    const available = (menu?.items ?? []).filter((i) => !soldOut.includes(i.name));

    return (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
                Sold-out control
            </p>
            <div className="flex gap-2 mb-3">
                <select
                    value={picker}
                    onChange={(e) => setPicker(e.target.value)}
                    disabled={disabled || !menu}
                    className="flex-1 min-w-0 h-9 rounded-md border border-gray-700 bg-gray-950 px-2 text-sm text-gray-200"
                >
                    <option value="">
                        {menu ? "Mark an item sold out…" : "Loading menu…"}
                    </option>
                    {available.map((i) => (
                        <option key={i.name} value={i.name}>
                            {i.name}
                        </option>
                    ))}
                </select>
                <Button
                    size="sm"
                    variant="outline"
                    className={cn("h-9 flex-shrink-0", DARK_OUTLINE_BTN)}
                    disabled={!picker || disabled}
                    onClick={() => {
                        if (!picker) return;
                        onApply([...soldOut, picker]);
                        setPicker("");
                    }}
                >
                    <PlusIcon className="w-4 h-4" />
                </Button>
            </div>
            {error && (
                <div className="mb-3 rounded-lg border border-rose-800 bg-rose-950/60 px-3 py-2.5">
                    <p className="text-xs text-rose-200">{error}</p>
                </div>
            )}
            {soldOut.length === 0 ? (
                <p className="text-xs text-gray-500">Everything is in stock.</p>
            ) : (
                <>
                    <ul className="space-y-1.5 mb-3 max-h-40 overflow-y-auto">
                        {soldOut.map((name) => (
                            <li
                                key={name}
                                className="flex items-center justify-between gap-2 text-sm text-gray-300"
                            >
                                <span className="truncate">{name}</span>
                                <button
                                    type="button"
                                    title="Back in stock"
                                    disabled={disabled}
                                    onClick={() => onApply(soldOut.filter((n) => n !== name))}
                                    className="p-1 text-gray-500 hover:text-gray-200 flex-shrink-0 disabled:opacity-50"
                                >
                                    <XIcon className="w-3.5 h-3.5" />
                                </button>
                            </li>
                        ))}
                    </ul>
                    <Button
                        size="sm"
                        variant="outline"
                        className={cn("w-full", DARK_OUTLINE_BTN)}
                        disabled={disabled}
                        onClick={() => onApply([])}
                    >
                        <RotateCcwIcon className="w-4 h-4" />
                        Reset all to in stock
                    </Button>
                </>
            )}
        </div>
    );
}
